import { Link } from "react-router-dom";

import { DateTime } from "luxon";
import { JSX } from "react/jsx-runtime";


import {
  getDateFromIso,
  getEnumValue,
  getReferentialLabel,
  getValue,
} from "@/_helpers/stringHelper.ts";
import { DataDwellingInfos } from "@/modules/dwelling/interfaces";
import { IDwelling } from "@/modules/dwelling/reducers";
import { DWELLING_DETAILS } from "@/modules/dwelling/routes";

import DwellingFeatureTable, {
  DwellingFeatureTableRow,
} from "./DwellingFeatureTable";
import { DwellingEquipmentTypeLabel } from "../../enums";

export interface DwellingPropertyProps {
  dwelling: DataDwellingInfos | IDwelling;
  extended: boolean;
}

const DwellingFeaturesProperty = ({
  dwelling,
  extended = false,
}: DwellingPropertyProps) => {
  const dwellingType = (): DwellingFeatureTableRow => {
    const dwellingType: string[] = [];

    if (dwelling.type) dwellingType.push(dwelling.type.label);
    if (dwelling.area) dwellingType.push(`${dwelling.area} m²`);
    if (dwelling.buildingNumber)
      dwellingType.push(`${dwelling.buildingNumber}`);
    if (dwelling.batchNumber) dwellingType.push(`${dwelling.batchNumber} lots`);
    return {
      title:
        dwelling.category?.value == "BUILD"
          ? "Nombre de batiment"
          : "Type de bien",
      state: dwellingType.join(" - "),
    };
  };

  const dwellingAddressInfos = (): DwellingFeatureTableRow => {
    const dwellingAddressInfos: string[] = [];
    if (dwelling.address.floor)
      dwellingAddressInfos.push(`Étage ${dwelling.address.floor}`);
    if (dwelling.address.entrance)
      dwellingAddressInfos.push(`Entrée ${dwelling.address.entrance}`);
    if (dwelling.address.door)
      dwellingAddressInfos.push(`Porte ${dwelling.address.door}`);
    if (dwelling.address.stair)
      dwellingAddressInfos.push(`Escalier ${dwelling.address.stair}`);
    return {
      title: "Infos",
      state: dwellingAddressInfos.join(", ") || "Aucun(e)s",
    };
  };

  const dwellingAddressOtherInfos = (): DwellingFeatureTableRow => {
    const dwellingAddressOtherInfos: string[] = [];
    if (dwelling.address.cellarNumber)
      dwellingAddressOtherInfos.push(
        `Cave n° ${dwelling.address.cellarNumber}`
      );
    if (dwelling.address.indoorParkingNumber)
      dwellingAddressOtherInfos.push(
        `Parking intérieur n° ${dwelling.address.indoorParkingNumber}`
      );
    if (dwelling.address.outdoorParkingNumber)
      dwellingAddressOtherInfos.push(
        `Parking extérieur n° ${dwelling.address.outdoorParkingNumber}`
      );
    if (dwelling.address.garageBoxNumber)
      dwellingAddressOtherInfos.push(`Box ${dwelling.address.garageBoxNumber}`);
    return {
      title: "Autres",
      state: dwellingAddressOtherInfos.join(" - ") || "Aucun(e)s",
    };
  };

  const dwellingDocumentsInfos = (): DwellingFeatureTableRow => {
    return {
      title: "Document(s) associé(s)",
      state: `${dwelling?.files?.length || 0}`,
    };
  };

  const dwellingConstructionPeriod = (): DwellingFeatureTableRow => {
    let dwellingConstructionPeriodTitle = "Période de construction";
    let dwellingConstructionPeriod = dwelling.constructionPeriod.label;
    if (
      undefined !== dwelling.constructionDate &&
      null !== dwelling.constructionDate
    ) {
      dwellingConstructionPeriodTitle = "Date de construction";
      dwellingConstructionPeriod = getDateFromIso(
        dwelling.constructionDate,
        DateTime.DATE_SHORT
      );
    }
    return {
      title: dwellingConstructionPeriodTitle,
      state: dwellingConstructionPeriod,
    };
  };
  const dwellingEquipments = () => {
    const equipments: DwellingFeatureTableRow[] = [];
    if (dwelling?.equipments?.length > 0) {
      for (const equipment of dwelling.equipments) {
        const state: string[] = [];
        equipment.isCollective
          ? state.push("Collectif")
          : state.push("Individuel");
        if (equipment.energies && equipment.energies.length > 0) {
          const energiesLabel: string[] = [];
          equipment.energies.forEach((ref) => {
            energiesLabel.push(ref.label);
          });
          state.push(energiesLabel.join(", "));
        }
        equipments.push({
          title: getEnumValue(DwellingEquipmentTypeLabel, equipment.type),
          state: state.join(" - "),
        });
      }
      return equipments.sort((a, b) => a.title.localeCompare(b.title));
    }
    return equipments;
  };

  const getExtendedValue = () => {
    let extendedValue: (
      | { title: string; state: JSX.Element }
      | { title: string; state: string }
    )[] = [];
    if (extended) {
      extendedValue = [
        {
          title: "Adresse",
          state: (
            <Link
              to={DWELLING_DETAILS.replace(
                ":id",
                dwelling.datedReference || dwelling.id
              )}
            >
              {dwelling.addressLabel}
            </Link>
          ),
        },
        {
          title: "Complément d'adresse",
          state: getValue(dwelling?.address?.address?.additionalAddress),
        },
      ];
    }
    return extendedValue;
  };

  const getExtendedValueLessor = () => {
    let extendedValue: (
      | { title: string; state: JSX.Element }
      | { title: string; state: string }
    )[] = [];
    if (extended) {
      extendedValue = [
        {
          title: "Nom du bailleur",
          state: getValue(dwelling?.lessor),
        },
        {
          title: "Résidence",
          state: getValue(dwelling?.address?.address?.residenceName),
        },
      ];
    }
    return extendedValue;
  };

  return (
    <div className="d-flex gap-3">
      <DwellingFeatureTable
        rows={[
          ...getExtendedValue(),
          {
            title: "Catégorie du bien",
            state: getReferentialLabel(dwelling.category),
          },
          dwellingType(),
          {
            title: "Digicode",
            state: getValue(dwelling.address.address.digicode),
          },
          dwellingAddressInfos(),
          dwellingConstructionPeriod(),
          dwellingAddressOtherInfos(),
          dwellingDocumentsInfos(),
        ]}
      />
      <DwellingFeatureTable
        rows={[
          ...getExtendedValueLessor(),
          {
            title: "N° de lot de gestion",
            state: getValue(dwelling?.managementLotNumber),
          },
          {
            title: "N° de lot de copropriété",
            state: getValue(dwelling?.condominiumNumber),
          },
          {
            title: "Type de propriété",
            state: dwelling.isCondominium
              ? "Copropriété"
              : "Propriété individuelle",
          },
          {
            title: "Statut du bien",
            state: dwelling.isFurnished ? (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => null}
                className="text-decoration-underline"
              >
                Location meublée
              </span>
            ) : (
              "Location nue"
            ),
          },
          ...dwellingEquipments(),
          {
            title: "État du bien",
            state: getReferentialLabel(dwelling.condition),
          },
        ]}
      />
    </div>
  );
};

export default DwellingFeaturesProperty;
